import { useCallback, useEffect, useState } from 'react';

import { BULK_APPLY_SCOPE } from '../utils/bulkApplyPlan';
import { isManualLotSelection, lotKeyOf } from '../utils/bulkLotNdcStaging';

const NO_BULK_APPLY = {
  isTicked: false,
  scope: BULK_APPLY_SCOPE.NEEDS_ATTENTION,
  untickedIds: [],
  isListOpen: false,
};

/**
 * POM-6537 — the "Also apply this lot to the other rows" choice in Change NDC & lot.
 *
 * Starts unticked on every row, and again whenever the NDC changes. Another lot is another list,
 * so the rows unticked for the last one do not carry over; nor do they across a change of scope.
 *
 * `selection` is what Apply would stage for this row, or null when the row has no move for the
 * others to follow (nothing valid picked yet, or its own lot).
 * `planBulkApplyFor` is the review dialog's, which owns the batch.
 */
const useBulkApplyChoice = ({ open, item, selectedNdc, selection, planBulkApplyFor }) => {
  const [choice, setChoice] = useState(NO_BULK_APPLY);
  const targetAllocation = selection?.lotAllocations?.[0] ?? null;
  const targetKey = lotKeyOf(targetAllocation);

  useEffect(() => {
    setChoice(NO_BULK_APPLY);
  }, [open, item?.requestLineItemId, selectedNdc]);

  useEffect(() => {
    setChoice((current) => ({ ...current, untickedIds: [] }));
  }, [targetKey]);

  const plan =
    open && item && planBulkApplyFor
      ? planBulkApplyFor({
          item,
          selectedNdc,
          selection,
          scope: choice.scope,
          untickedIds: choice.untickedIds,
        })
      : null;
  const isAvailable = !!plan && !plan.unavailableReason;
  const isApplied = choice.isTicked && isAvailable;
  const entries = isApplied ? plan.entries : [];
  const isManualLot = isManualLotSelection(targetAllocation);

  const setTicked = useCallback(
    (isTicked) =>
      setChoice((current) => ({ ...current, isTicked, untickedIds: [], isListOpen: false })),
    []
  );
  const setScope = useCallback(
    (scope) => setChoice((current) => ({ ...current, scope, untickedIds: [] })),
    []
  );
  const setRowTicked = useCallback(
    (requestLineItemId, isTicked) =>
      setChoice((current) => ({
        ...current,
        untickedIds: isTicked
          ? current.untickedIds.filter((id) => id !== requestLineItemId)
          : [...current.untickedIds, requestLineItemId],
      })),
    []
  );
  const toggleList = useCallback(
    () => setChoice((current) => ({ ...current, isListOpen: !current.isListOpen })),
    []
  );

  return {
    otherRowCount: plan?.otherRowCount ?? 0,
    isAvailable,
    isApplied,
    scope: choice.scope,
    isListOpen: choice.isListOpen,
    entries,
    lotNumber: targetAllocation?.lotNumber ?? null,
    isManualLot,
    editedFacilityName: item?.fulfillingPharmacyName ?? null,
    tickedRowsByOtherFacility: isApplied ? plan.tickedRowsByOtherFacility : [],
    rowsLeftOutByFacility: isApplied ? plan.rowsLeftOutByFacility : [],
    // This row's own claim, for the total the section states: all of a hand-recorded lot, else
    // what the batch leaves the picked lot short by.
    editedRowDeficitQty: isManualLot
      ? Number(targetAllocation?.quantity ?? 0)
      : Number(targetAllocation?.unverifiedQty ?? 0),
    // What Apply sends for the other rows, in the shape stageLotNdcChange takes.
    bulkChanges: entries
      .filter((entry) => entry.isTicked)
      .map((entry) => ({
        item: entry.row.item,
        requestLineItemId: entry.row.item.requestLineItemId,
        ...entry.change,
      })),
    setTicked,
    setScope,
    setRowTicked,
    toggleList,
  };
};

export default useBulkApplyChoice;
